import React from 'react';
import { PLAY_URL, APPSTORE_URL, INSTAGRAM_URL, TIKTOK_URL, PRIVACY_URL, TERMS_URL, CONTACT_EMAIL } from '../../config/storeLinks.js';
import { getBrowserLanguage, getForcedLanguageFromUrl } from '../../i18n/detectLanguage.js';

// Textos pós-compra (pt/en; outros idiomas caem em inglês). Usado na tela de
// sucesso e na página /app (a mesma que o botão do e-mail abre).
const COPY = {
  pt: { on: 'DISPONÍVEL NO', play: 'Google Play', download: 'Baixar na', store: 'App Store', soon: 'Em breve', privacy: 'Política de Privacidade', terms: 'Termos de Uso', rights: 'Todos os direitos reservados.' },
  en: { on: 'GET IT ON', play: 'Google Play', download: 'Download on the', store: 'App Store', soon: 'Coming soon', privacy: 'Privacy Policy', terms: 'Terms of Use', rights: 'All rights reserved.' },
};
export const postPurchaseLang = () => {
  const l = getForcedLanguageFromUrl() ?? getBrowserLanguage();
  return l === 'pt' ? 'pt' : 'en';
};
export const copyFor = (lang) => COPY[lang] ?? COPY.en;

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
    <polygon points="4,2.6 13.4,12 4,21.4" fill="#00B0FF" />
    <polygon points="4,2.6 16.6,9.8 13.4,12" fill="#00E676" />
    <polygon points="16.6,9.8 20.5,12 16.6,14.2 13.4,12" fill="#FFD600" />
    <polygon points="4,21.4 16.6,14.2 13.4,12" fill="#FF3D00" />
  </svg>
);
const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" fill="#000">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

const pill = 'flex items-center justify-center gap-3 w-full max-w-[320px] rounded-full bg-white text-black px-6 py-3 text-left';

export function StoreButtons({ lang }) {
  const c = copyFor(lang ?? postPurchaseLang());
  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <a href={PLAY_URL} className={pill}>
        <PlayIcon />
        <span className="leading-tight">
          <span className="block text-[11px] tracking-wide">{c.on}</span>
          <span className="block text-lg font-semibold">{c.play}</span>
        </span>
      </a>
      {APPSTORE_URL ? (
        <a href={APPSTORE_URL} className={pill}>
          <AppleIcon />
          <span className="leading-tight">
            <span className="block text-[11px]">{c.download}</span>
            <span className="block text-lg font-semibold">{c.store}</span>
          </span>
        </a>
      ) : (
        <div className={`${pill} opacity-60`} aria-disabled="true">
          <AppleIcon />
          <span className="leading-tight">
            <span className="block text-[11px]">{c.soon}</span>
            <span className="block text-lg font-semibold">{c.store}</span>
          </span>
        </div>
      )}
    </div>
  );
}

const SocialIcon = ({ href, label, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className="w-10 h-10 rounded-full bg-cyan text-bg flex items-center justify-center"
  >
    {children}
  </a>
);

export function PostPurchaseFooter({ lang }) {
  const c = copyFor(lang ?? postPurchaseLang());
  return (
    <div className="flex flex-col items-center gap-4 text-xs text-ink-muted mt-10">
      <div className="flex gap-3">
        <SocialIcon href={INSTAGRAM_URL} label="Instagram">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
        </SocialIcon>
        <SocialIcon href={TIKTOK_URL} label="TikTok">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M16.6 5.82s.51.5 0 0A4.278 4.278 0 0 1 15.54 3h-3.09v12.4a2.592 2.592 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6 0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.33 2.76 5.7 5.69 5.7 3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.24-1.48z" />
          </svg>
        </SocialIcon>
      </div>
      <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>
      <div className="flex gap-4">
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline">{c.privacy}</a>
        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline">{c.terms}</a>
      </div>
      <p>© 2026 FitClutch. {c.rights}</p>
    </div>
  );
}
